/**
 * Error Types - Comprehensive error handling types and utilities
 * Provides type-safe error handling throughout the application
 */

// Base error types
export enum ErrorType {
	WORKER_ERROR = "WORKER_ERROR",
	IMAGE_PROCESSING_ERROR = "IMAGE_PROCESSING_ERROR",
	SVG_EXPORT_ERROR = "SVG_EXPORT_ERROR",
	VALIDATION_ERROR = "VALIDATION_ERROR",
	NETWORK_ERROR = "NETWORK_ERROR",
	FILE_SYSTEM_ERROR = "FILE_SYSTEM_ERROR",
}

export interface AppError {
	type: ErrorType;
	message: string;
	code?: string | number;
	details?: unknown;
	timestamp: number;
}

// Specific error interfaces
export interface WorkerError extends AppError {
	type: ErrorType.WORKER_ERROR;
	workerId?: string;
	taskId?: string;
}

export interface ImageProcessingError extends AppError {
	type: ErrorType.IMAGE_PROCESSING_ERROR;
	fileName?: string;
	fileSize?: number;
}

export interface SvgExportError extends AppError {
	type: ErrorType.SVG_EXPORT_ERROR;
	exportFormat?: string;
}

export interface ValidationError extends AppError {
	type: ErrorType.VALIDATION_ERROR;
	field?: string;
	value?: unknown;
}

// Union type for all application errors
export type ApplicationError =
	| WorkerError
	| ImageProcessingError
	| SvgExportError
	| ValidationError
	| AppError;

// Error factory functions
export const createWorkerError = (
	message: string,
	details?: { workerId?: string; taskId?: string; originalError?: unknown },
): WorkerError => ({
	type: ErrorType.WORKER_ERROR,
	message,
	workerId: details?.workerId,
	taskId: details?.taskId,
	details: details?.originalError,
	timestamp: Date.now(),
});

export const createImageProcessingError = (
	message: string,
	details?: { fileName?: string; fileSize?: number; originalError?: unknown },
): ImageProcessingError => ({
	type: ErrorType.IMAGE_PROCESSING_ERROR,
	message,
	fileName: details?.fileName,
	fileSize: details?.fileSize,
	details: details?.originalError,
	timestamp: Date.now(),
});

export const createSvgExportError = (
	message: string,
	details?: { exportFormat?: string; originalError?: unknown },
): SvgExportError => ({
	type: ErrorType.SVG_EXPORT_ERROR,
	message,
	exportFormat: details?.exportFormat,
	details: details?.originalError,
	timestamp: Date.now(),
});

export const createValidationError = (
	message: string,
	details?: { field?: string; value?: unknown; originalError?: unknown },
): ValidationError => ({
	type: ErrorType.VALIDATION_ERROR,
	message,
	field: details?.field,
	value: details?.value,
	details: details?.originalError,
	timestamp: Date.now(),
});

// Type guards
export const isWorkerError = (error: ApplicationError): error is WorkerError =>
	error.type === ErrorType.WORKER_ERROR;

export const isImageProcessingError = (
	error: ApplicationError,
): error is ImageProcessingError =>
	error.type === ErrorType.IMAGE_PROCESSING_ERROR;

export const isSvgExportError = (
	error: ApplicationError,
): error is SvgExportError => error.type === ErrorType.SVG_EXPORT_ERROR;

export const isValidationError = (
	error: ApplicationError,
): error is ValidationError => error.type === ErrorType.VALIDATION_ERROR;

// Error handling utilities
export const formatErrorMessage = (error: ApplicationError): string => {
	const baseMessage = `[${error.type}] ${error.message}`;

	if (isWorkerError(error) && error.taskId) {
		return `${baseMessage} (Task: ${error.taskId})`;
	}

	if (isImageProcessingError(error) && error.fileName) {
		return `${baseMessage} (File: ${error.fileName})`;
	}

	if (isValidationError(error) && error.field) {
		return `${baseMessage} (Field: ${error.field})`;
	}

	return baseMessage;
};

export const getErrorSeverity = (
	error: ApplicationError,
): "low" | "medium" | "high" | "critical" => {
	switch (error.type) {
		case ErrorType.WORKER_ERROR:
			return "high";
		case ErrorType.IMAGE_PROCESSING_ERROR:
			return "medium";
		case ErrorType.SVG_EXPORT_ERROR:
			return "medium";
		case ErrorType.VALIDATION_ERROR:
			return "low";
		case ErrorType.NETWORK_ERROR:
			return "medium";
		case ErrorType.FILE_SYSTEM_ERROR:
			return "high";
		default:
			return "medium";
	}
};

// Result type for operations that can fail
export type Result<T, E = ApplicationError> =
	| {
			success: true;
			data: T;
	  }
	| {
			success: false;
			error: E;
	  };

export const createSuccessResult = <T>(data: T): Result<T> => ({
	success: true,
	data,
});

export const createErrorResult = <T, E = ApplicationError>(
	error: E,
): Result<T, E> => ({
	success: false,
	error,
});
